import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { ApiError } from '@rydres/api-client';
import { BRAND, describeStatus, tcpLabel, type Point } from '@rydres/shared';
import { Badge, Button, Confirm, InfoTip, Input, MapPreview, Notice, Spinner, fmtDateTime, fmtPhone, formatUsd, useRoute } from '@rydres/ui';
import { api, geoApi, STRIPE_PUBLISHABLE_KEY } from '../lib/api.js';
import { approveReferral, refuseReferral, usePassenger } from '../lib/passenger.js';
import type { PublicBookingView } from '../lib/types.js';
import { AppBadges, appLinksShown } from './AppBadges.js';
import { Backups } from './Backups.js';

type Offer = Extract<NonNullable<PublicBookingView['booking']['referral']>, { status: 'offer' }>;
type Tip = NonNullable<PublicBookingView['booking']['tip']>;

const stripePromise = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null;

/** After a completed ride: a tip by card, a share of the fare or the rider's own amount. */
function TipCard({ token, tip, name }: { token: string; tip: Tip; name: string }) {
  const [picked, setPicked] = useState<number | 'own' | null>(null);
  const [own, setOwn] = useState('');
  const [secret, setSecret] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cents = picked === 'own' ? Math.round(Number(own) * 100) : picked;
  const valid = typeof cents === 'number' && cents >= tip.minCents && cents <= tip.maxCents;
  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.call<PublicBookingView>('POST', `/public/bookings/${token}/tip`, { auth: false, body: { amountCents: cents } });
      setSecret(r.clientSecret ?? null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work. Please try again.');
    } finally {
      setBusy(false);
    }
  };
  const chip = (on: boolean) => `rounded-xl px-2 py-3 text-sm font-bold ${on ? 'bg-brand text-on-brand' : 'bg-card2 text-ink'}`;
  return (
    <section aria-label={`Add a tip for ${name}`} className="mt-4 space-y-3 rounded-2xl border border-line bg-card p-4">
      <h2 className="flex items-center gap-2 font-black">
        Add a tip for {name}
        <InfoTip label="Tips">Tips are up to you. A tip goes to your driver's business, less Stripe's card fee. {BRAND.name} keeps none of it. If you would rather tip in cash, you can.</InfoTip>
      </h2>
      {secret && stripePromise ? (
        <Elements stripe={stripePromise} options={{ clientSecret: secret, appearance: { theme: 'stripe', variables: { colorPrimary: '#2563eb', borderRadius: '12px' } } }}>
          <TipPayForm token={token} cents={cents as number} onBack={() => setSecret(null)} />
        </Elements>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {tip.suggestions.map((s) => (
              <button key={s.percent} type="button" aria-pressed={picked === s.cents} onClick={() => setPicked(s.cents)} className={chip(picked === s.cents)}>{s.percent}% · {formatUsd(s.cents)}</button>
            ))}
            <button type="button" aria-pressed={picked === 'own'} onClick={() => setPicked('own')} className={chip(picked === 'own')}>Other</button>
          </div>
          {picked === 'own' && (
            <label className="block text-sm">
              <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-muted">Your amount, $</span>
              <Input autoFocus inputMode="decimal" value={own} onChange={(e) => setOwn(e.target.value.replace(/[^\d.]/g, ''))} />
              <span className="mt-1 block text-xs text-muted">From {formatUsd(tip.minCents)} to {formatUsd(tip.maxCents)}.</span>
            </label>
          )}
          {error && <Notice tone="danger">{error}</Notice>}
          <Button className="w-full" disabled={busy || !valid} onClick={() => void start()}>{busy ? 'One moment…' : valid ? `Add a ${formatUsd(cents as number)} tip` : 'Choose an amount'}</Button>
        </>
      )}
    </section>
  );
}

function TipPayForm({ token, cents, onBack }: { token: string; cents: number; onBack: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const pay = async () => {
    if (!stripe || !elements) return;
    setBusy(true);
    const { error: err } = await stripe.confirmPayment({ elements, confirmParams: { return_url: `${location.origin}/b/${token}?paid=1&tip=1` } });
    if (err) {
      setError(err.message ?? 'Payment did not go through.');
      setBusy(false);
    }
  };
  return (
    <div className="space-y-4">
      <PaymentElement options={{ layout: 'tabs' }} />
      {error && <Notice tone="danger">{error}</Notice>}
      <Button className="w-full" disabled={busy || !stripe} onClick={() => void pay()}>{busy ? 'Processing…' : `Pay ${formatUsd(cents)} tip`}</Button>
      <Button variant="ghost" className="w-full" disabled={busy} onClick={onBack}>Change the amount</Button>
    </div>
  );
}

/** What the rider sees once they have tipped. */
function tipLine(paid: NonNullable<Tip['paid']>): string {
  if (paid.status === 'refunded') return `Your ${formatUsd(paid.amountCents)} tip was refunded.`;
  if (paid.refundedCents) return `You added a ${formatUsd(paid.amountCents)} tip, and ${formatUsd(paid.refundedCents)} of it was refunded.`;
  return `You added a ${formatUsd(paid.amountCents)} tip. Thank you!`;
}

/** A backup driver offered to the rider, with what they need to decide; only their sign-in can answer. */
function ReferralOffer({ offer, onRefused }: { offer: Offer; onRefused: (view: PublicBookingView) => void }) {
  const navigate = useNavigate();
  const passenger = usePassenger();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const d = offer.driver;
  const name = d.displayName ?? d.legalName ?? 'This driver';
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.');
      setBusy(false);
    }
  };
  return (
    <section aria-label={`${name} can take your ride`} className="mt-4 space-y-3 rounded-2xl border border-accent bg-card p-4">
      <h2 className="font-black">{name} can take your ride</h2>
      <Link to={d.handle ? `/me/drivers/${d.handle}` : '#'} aria-label={`About ${name}`} className="flex items-center gap-3">
        {d.photoUrl ? (
          <img src={d.photoUrl} alt={name} className="h-16 w-16 shrink-0 rounded-2xl object-cover" />
        ) : (
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-brand text-2xl font-black text-on-brand">{name.slice(0, 1)}</div>
        )}
        <div className="min-w-0">
          <div className="font-bold">{name}</div>
          <div className="text-sm text-ink2">{d.legalName}</div>
          <div className="text-xs text-muted">{d.tcpNumber}</div>
        </div>
      </Link>
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm text-ink2">Their price, per vehicle, until {fmtDateTime(offer.expiresAt)}</span>
        <span className="text-2xl font-black">{formatUsd(offer.priceCents ?? 0)}</span>
      </div>
      <p className="text-xs text-muted">{d.cancelPolicyText}</p>
      <p className="text-xs text-muted">If you accept, your contract is with {d.legalName ?? name}. They get your name and number, and you pay or agree on the next screen.</p>
      {error && <Notice tone="danger">{error}</Notice>}
      {passenger.loading ? null : passenger.me ? (
        <div className="grid gap-2">
          <Button disabled={busy} onClick={() => void run(async () => navigate(`/q/${(await approveReferral(offer.id)).quoteToken}`))}>Continue with {name}</Button>
          <Button variant="ghost" disabled={busy} onClick={() => void run(async () => { onRefused(await refuseReferral(offer.id)); setBusy(false); })}>No thanks</Button>
        </div>
      ) : (
        <Link to={`/me?back=${encodeURIComponent(location.pathname)}`} className="block rounded-xl bg-brand px-4 py-3 text-center font-bold text-on-brand brand-press">Sign in to answer</Link>
      )}
    </section>
  );
}

export function StatusPage() {
  const { token = '' } = useParams();
  const [params] = useSearchParams();
  const [view, setView] = useState<PublicBookingView | null | undefined>(undefined);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const load = () =>
    api.call<PublicBookingView>('GET', `/public/bookings/${token}`, { auth: false }).then(setView, () => setView(null));

  useEffect(() => {
    void load();
    // Right after paying, the webhook may land a few seconds later: poll briefly.
    if (params.get('paid') || params.get('redirect_status')) {
      let n = 0;
      const timer = setInterval(() => {
        void load();
        if (++n >= 15) clearInterval(timer);
      }, 2000);
      return () => clearInterval(timer);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (view === undefined) return <div className="flex min-h-screen items-center justify-center"><Spinner /></div>;
  if (view === null) return <div className="p-6 text-muted">We could not find that booking.</div>;
  const { booking: b, driver: d } = view;
  const awaitingPayment = b.status === 'requested' && b.paymentMode === 'card';
  const look = describeStatus(b);

  const cancel = async () => {
    setConfirming(false);
    try {
      setView(await api.call<PublicBookingView>('POST', `/public/bookings/${token}/cancel`, { auth: false }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not cancel.');
    }
  };

  return (
    <div className="mx-auto max-w-xl px-4 pb-16 pt-8">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-black">Your ride</h1>
        <Badge value={awaitingPayment ? 'awaiting payment' : look.label} tone={look.tone} />
      </div>
      <Link to={d.handle ? `/me/drivers/${d.handle}` : '#'} aria-label={`About ${d.displayName ?? 'your driver'}`} className="mt-3 flex items-center gap-3">
        {d.photoUrl ? (
          <img src={d.photoUrl} alt="" className="h-14 w-14 shrink-0 rounded-2xl object-cover" />
        ) : (
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-brand text-xl font-black text-on-brand">{(d.displayName ?? '?').slice(0, 1)}</div>
        )}
        <div className="min-w-0">
          <div className="font-bold">{d.displayName}</div>
          <div className="text-sm text-ink2">{d.legalName} · {tcpLabel(d.tcpNumber)}</div>
        </div>
        <span className="ml-auto text-muted" aria-hidden="true">›</span>
      </Link>

      {awaitingPayment && <div className="mt-4"><Notice tone="warn">Waiting for your payment to be confirmed. This usually takes a few seconds.</Notice></div>}
      {b.status === 'requested' && b.paymentMode !== 'card' && (
        <div className="mt-4"><Notice tone="info">{b.kind === 'custom' ? `${d.displayName} has your request and will send you a price.` : `${d.displayName} has your request and will answer shortly.`}</Notice></div>
      )}
      {b.status === 'quoted' && (
        <div className="mt-4 space-y-2">
          <Notice tone="info">{d.displayName} accepted your request at {formatUsd(b.priceCents ?? 0)}. It is confirmed once you agree to the price.</Notice>
          {b.quoteToken && <Link to={`/q/${b.quoteToken}`} className="block rounded-xl bg-brand px-4 py-3 text-center font-bold text-on-brand brand-press">See the price</Link>}
        </div>
      )}
      {b.referral?.status === 'checking' && <div className="mt-4"><Notice tone="info">{d.displayName} cannot take this ride and is asking a driver they trust. You choose whether to accept.</Notice></div>}
      {b.referral?.status === 'offer' && <ReferralOffer offer={b.referral} onRefused={setView} />}
      {b.referral?.status === 'approved' && <div className="mt-4 space-y-2"><Notice tone="info">You chose another driver for this ride.</Notice><Link to={`/b/${b.referral.manageToken}`} className="block rounded-xl bg-brand px-4 py-3 text-center font-bold text-on-brand brand-press">See your ride</Link></div>}
      {b.status === 'expired' && <div className="mt-4"><Notice tone="warn">This request expired without a confirmed price.{d.handle && <> You can <a className="underline" href={`/${d.handle}`}>ask again</a>.</>}</Notice></div>}
      {b.status === 'cancelled' && <div className="mt-4"><Notice tone="warn">This booking was cancelled{b.cancelReason ? `: ${b.cancelReason}` : '.'}</Notice></div>}
      {b.status === 'declined' && <div className="mt-4"><Notice tone="warn">{d.displayName} cannot take this ride{b.cancelReason ? `: ${b.cancelReason}` : '.'}</Notice></div>}
      {(b.status === 'declined' || (b.status === 'cancelled' && b.cancelledBy === 'driver')) && <Backups list={d.backups} driverName={d.displayName} title={`Still need a ride? Drivers ${d.displayName ?? 'your driver'} trusts`} />}

      <RideMap from={b.pickupPoint} to={b.dropoffPoint} />
      <dl className="mt-6 grid grid-cols-[auto_1fr] gap-x-4 gap-y-3 rounded-2xl border border-line bg-card p-4 text-sm">
        <dt className="text-muted">Pickup</dt>
        <dd className="font-bold">{fmtDateTime(b.startsAt)}<div className="font-medium text-ink2">{b.pickupAddress}</div></dd>
        <dt className="text-muted">Drop-off</dt>
        <dd className="font-medium">{b.dropoffAddress}</dd>
        <dt className="text-muted">Passengers</dt>
        <dd className="font-medium">{b.passengers}</dd>
        {b.notes && (
          <>
            <dt className="text-muted">Instructions</dt>
            <dd className="whitespace-pre-line font-medium">{b.notes}</dd>
          </>
        )}
        {b.status !== 'handed_off' && (
          <>
            <dt className="text-muted">Price</dt>
            <dd className="font-bold">
              {b.priceCents == null && b.status === 'referred' ? (
                <span className="font-medium text-ink2">Not set yet</span>
              ) : b.priceCents == null ? (
                <span className="font-medium text-ink2">{b.status === 'requested' ? `Not set yet. ${d.displayName} will send you a price.` : 'Not set'}</span>
              ) : (
                formatUsd(b.priceCents)
              )}
              <div className="font-medium text-ink2">
                {b.priceCents == null || b.status === 'quoted'
                  ? ''
                  : b.paymentMode === 'card'
                  ? b.payment?.status === 'succeeded'
                    ? 'Paid by card'
                    : b.payment?.status === 'refunded'
                      ? 'Refunded'
                      : b.payment?.status === 'partially_refunded'
                        ? `Refunded ${formatUsd(b.payment.refundedCents)}`
                        : 'Card'
                  : `Pay the driver directly${d.zelleHandle ? ` · Zelle ${d.zelleHandle}` : ''}`}
              </div>
            </dd>
          </>
        )}
        <dt className="text-muted">Driver</dt>
        <dd className="font-medium">
          <a className="text-accent-text underline" href={`tel:${d.phone ?? ''}`}>{fmtPhone(d.phone)}</a>
          {d.handle && (b.status === 'requested' || b.status === 'quoted' || b.status === 'confirmed') && (
            <> · <Link className="text-accent-text underline" to={`/me/people?with=${encodeURIComponent(d.handle)}`}>Message {d.displayName ?? 'the driver'}</Link></>
          )}
        </dd>
      </dl>

      {b.tip?.paid ? (
        <div className="mt-4"><Notice tone="ok">{tipLine(b.tip.paid)}</Notice></div>
      ) : params.get('tip') && params.get('redirect_status') !== 'failed' ? (
        <div className="mt-4"><Notice tone="info">Thank you! Your tip shows here in a moment.</Notice></div>
      ) : b.tip?.open && stripePromise ? (
        <>
          {params.get('tip') && <div className="mt-4"><Notice tone="danger">That card did not go through, so no tip was added. You can try again.</Notice></div>}
          <TipCard token={token} tip={b.tip} name={d.displayName ?? 'your driver'} />
        </>
      ) : null}

      <div className="mt-4 space-y-2">
        {b.payment?.receiptUrl && (
          <a className="block text-sm text-accent-text underline" href={b.payment.receiptUrl} target="_blank" rel="noreferrer">Card receipt</a>
        )}
        {b.tip?.paid?.receiptUrl && (
          <a className="block text-sm text-accent-text underline" href={b.tip.paid.receiptUrl} target="_blank" rel="noreferrer">Tip receipt</a>
        )}
        {b.waybillUrl && (
          <a className="block text-sm text-accent-text underline" href={b.waybillUrl} target="_blank" rel="noreferrer">Trip record (waybill)</a>
        )}
      </div>

      {(b.status === 'requested' || b.status === 'quoted' || b.status === 'referred' || b.status === 'confirmed') && (
        <div className="mt-8">
          <p className="mb-2 text-xs text-muted">{d.cancelPolicyText}</p>
          <Button variant="danger" className="w-full" onClick={() => setConfirming(true)}>{b.status === 'confirmed' ? 'Cancel this ride' : 'Cancel this request'}</Button>
        </div>
      )}
      {error && <div className="mt-3"><Notice tone="danger">{error}</Notice></div>}
      {confirming && (
        <Confirm title={b.status === 'confirmed' ? 'Cancel this ride?' : 'Cancel this request?'} body={d.cancelPolicyText} confirmLabel="Yes, cancel" danger onConfirm={() => void cancel()} onCancel={() => setConfirming(false)} />
      )}
      {appLinksShown() && b.status !== 'cancelled' && b.status !== 'expired' && b.status !== 'declined' && b.status !== 'handed_off' && (
        <section className="mt-8 space-y-3 rounded-2xl border border-line bg-card p-4">
          <h2 className="font-bold">Follow this ride in the {BRAND.name} app</h2>
          <p className="text-sm text-ink2">
            Updates arrive as notifications instead of texts, you can message {d.displayName ?? 'your driver'}, and booking again takes two taps. Sign in with the mobile number you booked with.
          </p>
          <AppBadges />
        </section>
      )}
      <footer className="mt-12 text-center text-xs text-muted">Powered by {BRAND.name}, operated by {BRAND.operator}. The driver provides the transportation.</footer>
    </div>
  );
}

/** Pickup and drop-off on a small map, with the road between them, when the places were picked on the map. */
function RideMap({ from, to }: { from: Point | null; to: Point | null }) {
  const route = useRoute(geoApi, from, to);
  if (!from && !to) return null;
  return <MapPreview className="mt-6" from={from} to={to} line={route?.polyline} />;
}
